// src/store/githubSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface GithubState {
  connected: boolean;
  username: string | null;

  repoName: string | null;
  repoUrl: string | null;
  repoVisibility: "private" | "public" | null;
  repoOrganization: string | null;
  github_2way_sync: "yes" | "no" | null;

  // NEW UI state
  subTab: "actions" | "commits";
  refreshTrigger: number; // increments to force refetch
}

const initialState: GithubState = {
  connected: false,
  username: null,
  repoName: null,
  repoUrl: null,
  repoVisibility: null,
  repoOrganization: null,
  github_2way_sync: null,

  // NEW defaults
  subTab: "actions",
  refreshTrigger: 0,
};

const githubSlice = createSlice({
  name: "github",
  initialState,
  reducers: {
    setGithubConnected: (state, action: PayloadAction<boolean>) => {
      state.connected = action.payload;
    },
    setGithubUsername: (state, action: PayloadAction<string | null>) => {
      state.username = action.payload;
    },

    setGithubRepoInfo: (
      state,
      action: PayloadAction<{
        repoName: string | null;
        repoUrl: string | null;
        repoVisibility: "private" | "public" | null;
        repoOrganization: string | null;
        github_2way_sync?: "yes" | "no" | null;
      }>
    ) => {
      state.repoName = action.payload.repoName;
      state.repoUrl = action.payload.repoUrl;
      state.repoVisibility = action.payload.repoVisibility;
      state.repoOrganization = action.payload.repoOrganization;

      if (typeof action.payload.github_2way_sync !== "undefined") {
        state.github_2way_sync = action.payload.github_2way_sync;
      }
    },

    // NEW: Sub-tab switcher
    setGithubSubTab: (state, action: PayloadAction<"actions" | "commits">) => {
      state.subTab = action.payload;
    },

    // NEW: Used by refresh button to trigger a refetch
    triggerGithubCommitsRefresh: (state) => {
      state.refreshTrigger += 1;
    },

    resetGithub: (state) => {
      state.connected = false;
      state.username = null;
      state.repoName = null;
      state.repoUrl = null;
      state.repoVisibility = null;
      state.repoOrganization = null;
      state.github_2way_sync = null;
      state.subTab = "actions";
      state.refreshTrigger = 0;
    },
  },
});

export const {
  setGithubConnected,
  setGithubUsername,
  setGithubRepoInfo,
  setGithubSubTab,
  triggerGithubCommitsRefresh,
  resetGithub,
} = githubSlice.actions;

export default githubSlice.reducer;
